import { useCallback, useMemo } from 'react';
import { trimSlashes } from 'kineticUtils/routing';

export type RouteBuilder = (basePath?: string) => string;

/**
 * This builder should be used to slim down Router code.
 *
 * creating a route builder:
 *
 * `const route = useRouteBuilder('integrations')`
 *
 * using the builder helper:
 *
 * `route() => '/integrations/'`
 *
 * ↑ is the same as ↓
 *
 * `route('/') => '/integrations/'`
 *
 * `route('pathName') => '/integrations/pathName/'`
 *
 * ↑ is the same as ↓
 *
 * `route('/pathName/') => '/integrations/pathName/'`
 *
 * use wildcard (*) to make default routes to fallback to a root route
 *
 * `route('*') => '/integrations*'`
 *
 * hash routes don't have trailing slash
 *
 * We wont need this with react-router-dom v6
 *
 * See: https://reacttraining.com/blog/react-router-v6-pre/#introducing-routes
 *
 * @param basePath the base of the routes you want to build. Do not need to prefix or suffix with "/"
 * @returns helper function to create routing paths
 */
export const useRouteBuilder = (basePath: string) => {
  // trim any leading or trailing "/"
  const fixedBasePath = useMemo(() => trimSlashes(basePath), [basePath]);

  return useCallback(
    (path?: string) => {
      const rootPath = `/${fixedBasePath}`;
      if (!path) {
        return `${rootPath}/`;
      }
      const trimmedPath = trimSlashes(path);
      const splitPath = trimmedPath.split('/');
      const lastDirectory = splitPath[splitPath.length - 1];
      const isHashPath = lastDirectory[0] === '#';

      // this lets us redirect both "integrations/someRoute" and "integrations" to "integrations/"
      const isWildCard = trimmedPath === '*';
      const suffix = isWildCard ? trimmedPath : `/${trimmedPath}${isHashPath ? '' : '/'}`;
      return `${rootPath}${suffix}`;
    },
    [fixedBasePath]
  );
};
